import { Link } from 'react-router-dom'
import { FiCheckCircle, FiAlertTriangle } from 'react-icons/fi'
import BlogLayout from '../../components/BlogLayout'

const toc = [
  { id: 'concepto', label: 'Concepto: dos cosas que se confunden' },
  { id: 'registro-jornada', label: 'Registro de jornada: lo que exige la ley' },
  { id: 'control-horario', label: 'Control horario: lo que necesita la empresa' },
  { id: 'tabla-comparativa', label: 'Tabla comparativa' },
  { id: 'por-que-importa', label: 'Por qué la diferencia importa' },
  { id: 'faq', label: 'Preguntas frecuentes' },
]

const faqs = [
  { question: '¿Son lo mismo registro horario y registro de jornada?', answer: 'Sí. La ley usa la expresión "registro de jornada" (art. 34.9 ET), pero coloquialmente se le llama "registro horario". Son sinónimos en castellano jurídico.' },
  { question: '¿Y control horario?', answer: 'Control horario es el concepto amplio: la actividad de la empresa de gestionar y supervisar el tiempo de trabajo. Incluye el registro de jornada como núcleo legal, más turnos, ausencias, horas extras, banca de horas, vacaciones...' },
  { question: '¿Puedo limitarme al registro de jornada?', answer: 'Legalmente sí. Pero en empresas con turnos, varios centros, teletrabajo o más de 5 empleados, sin el resto del control horario tendrás reclamaciones de horas extras, conflictos de turnos y más coste administrativo del que ahorras.' },
  { question: '¿Cuál es la frontera práctica?', answer: 'El registro de jornada termina cuando registras inicio y fin con trazabilidad. El control horario empieza cuando además gestionas la planificación, los saldos y los procesos asociados.' },
  { question: '¿Existe alguna sanción por no tener "control horario"?', answer: 'No, las sanciones LISOS son solo por no tener registro de jornada. El resto son buenas prácticas que reducen riesgos y costes operativos, pero no están tipificadas.' },
]

export default function DiferenciaControlHorarioRegistroJornada() {
  return (
    <BlogLayout slug="diferencia-control-horario-registro-jornada" toc={toc} faqs={faqs}>
      <section id="concepto" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Concepto: dos cosas que se confunden</h2>
        <p>
          En el día a día oirás hablar indistintamente de "registro horario", "registro de jornada", "fichaje", "control horario" o "control de presencia". A efectos prácticos los tres primeros son lo mismo. Los dos últimos son <strong>algo distinto y más amplio</strong>.
        </p>
        <p>
          Confundirlos no es inofensivo: hay empresas que creen estar cumpliendo porque tienen "control horario" cuando lo que la ley les exige es "registro de jornada", y viceversa.
        </p>
      </section>

      <section id="registro-jornada" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Registro de jornada: lo que exige la ley</h2>
        <p>
          El <strong>registro de jornada</strong> es un concepto jurídico estricto definido por el art. 34.9 del Estatuto de los Trabajadores tras la reforma del RD-ley 8/2019, y reforzado ahora por <Link to="/blog/real-decreto-2026-fichaje-digital">el nuevo Real Decreto de fichaje digital previsto para 2026</Link>. Su contenido mínimo:
        </p>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Hora de inicio y de finalización de la jornada de cada empleado.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Conservación durante 4 años.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Disponibilidad para los trabajadores, sus representantes y la Inspección.</li>
        </ul>
        <p>
          Es lo que verifica la ITSS y lo que sanciona si falta. Todo lo demás (cómo se hace, con qué tecnología, qué pausas se anotan, qué calendario aplica) es libertad de la empresa.
        </p>
      </section>

      <section id="control-horario" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Control horario: lo que necesita la empresa</h2>
        <p>
          El <strong>control horario</strong> es el conjunto de prácticas, procesos y herramientas con los que la empresa organiza el tiempo de trabajo. Incluye el registro de jornada, pero va mucho más allá:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Planificación</h3>
            <p className="text-sm text-gray-600">Calendarios laborales, turnos rotativos, jornadas partidas, festivos por centro.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Gestión de ausencias</h3>
            <p className="text-sm text-gray-600">Vacaciones, bajas médicas, permisos retribuidos, asuntos propios.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Horas extras y banca</h3>
            <p className="text-sm text-gray-600">Cómputo, autorización, retribución o compensación con descanso. <Link to="/blog/banca-horas-vs-horas-extras">Diferenciar bien banca de horas y horas extras</Link> evita reclamaciones costosas.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Análisis e informes</h3>
            <p className="text-sm text-gray-600">Productividad por centro, picos de trabajo, rotación, costes laborales.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Cumplimiento normativo extendido</h3>
            <p className="text-sm text-gray-600">Descansos mínimos, jornada nocturna, prevención de riesgos psicosociales.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">Integración con nómina</h3>
            <p className="text-sm text-gray-600">Cálculo automático de horas trabajadas y exportación a programas de nómina.</p>
          </div>
        </div>
      </section>

      <section id="tabla-comparativa" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Tabla comparativa</h2>
        <div className="overflow-x-auto my-6">
          <table className="min-w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 font-semibold text-gray-900"></th>
                <th className="text-left p-3 font-semibold text-gray-900">Registro de jornada</th>
                <th className="text-left p-3 font-semibold text-gray-900">Control horario</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="p-3 font-medium">Naturaleza</td><td className="p-3">Obligación legal</td><td className="p-3">Buena práctica empresarial</td></tr>
              <tr><td className="p-3 font-medium">Norma</td><td className="p-3">Art. 34.9 ET / RD-ley 8/2019</td><td className="p-3">Sin norma específica</td></tr>
              <tr><td className="p-3 font-medium">Alcance</td><td className="p-3">Inicio y fin de jornada</td><td className="p-3">Toda la gestión del tiempo</td></tr>
              <tr><td className="p-3 font-medium">Beneficiarios</td><td className="p-3">Trabajador + ITSS</td><td className="p-3">Empresa + RR. HH. + nómina</td></tr>
              <tr><td className="p-3 font-medium">Sanción si falta</td><td className="p-3">751 € a 187.515 €</td><td className="p-3">Ninguna directa</td></tr>
              <tr><td className="p-3 font-medium">Conservación</td><td className="p-3">4 años</td><td className="p-3">Variable según finalidad</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="por-que-importa" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Por qué la diferencia importa</h2>
        <p>
          Confundir los dos conceptos genera dos errores típicos en sentidos opuestos:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-5">
            <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2"><FiAlertTriangle className="h-5 w-5" /> Error A: tengo control horario, ergo cumplo</h3>
            <p className="text-sm text-amber-800">Algunas empresas con sistemas antiguos de "control de presencia" (tarjeta + tornos) creen que con eso basta. Si los registros no son fiables, no son accesibles al trabajador o no se conservan 4 años, no cumple el 34.9 ET aunque "controle".</p>
          </div>
          <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-5">
            <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2"><FiAlertTriangle className="h-5 w-5" /> Error B: cumplo el registro, ergo no necesito más</h3>
            <p className="text-sm text-amber-800">Otras empresas se limitan a un Excel cumpliendo el mínimo y se ven sepultadas por reclamaciones de horas extras, fricción con turnos y falta de visibilidad. Cumplen pero no controlan.</p>
          </div>
        </div>
        <p>
          Lo eficiente es construir el control horario sobre un buen registro de jornada digital. Cumples la ley sin esfuerzo y aprovechas la inversión para hacer mejor RR. HH.
        </p>
      </section>

      <section id="faq" className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div key={f.question}>
              <h3 className="font-semibold text-gray-900 mb-1">{f.question}</h3>
              <p className="text-gray-600 text-sm">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-gray-100 pt-8 mt-12 text-sm text-gray-500">
        Para profundizar:
        <Link to="/blog/registro-horario-obligatorio-espana" className="text-blue-600 hover:underline mx-1">guía completa del registro horario</Link>
        ·
        <Link to="/control-horario-empresas" className="text-blue-600 hover:underline mx-1">página pilar de control horario</Link>.
      </div>
    </BlogLayout>
  )
}
